import { useEffect } from 'react';

const SW_RELOAD_GUARD_KEY = 'kma_sw_reloaded_once';

/**
 * Hook to automatically clear cache on app lifecycle events
 * - Clears cache when a new version is available
 * - Optionally clears cache on app visibility change (when user returns to app)
 */
export function useCacheManager(
  options: {
    clearOnVisibilityChange?: boolean;
    clearOnFocus?: boolean;
  } = {}
): { clearCache: () => Promise<void>; checkForUpdates: () => void } {
  const { clearOnVisibilityChange = false, clearOnFocus = false } = options;

  useEffect(() => {
    const hadControllerOnInit = Boolean(navigator.serviceWorker?.controller);
    let hasControllerChanged = false;

    const handleControllerChange = (): void => {
      // Avoid reload on first-time SW control (fresh visitors with clientsClaim enabled).
      // Only reload for real updates when the page already had an active controller.
      if (!hadControllerOnInit) return;

      // Fail-safe strategy: if sessionStorage is unavailable, skip forced reload
      // to avoid potential refresh loops in restricted browser modes.
      try {
        if (sessionStorage.getItem(SW_RELOAD_GUARD_KEY) === '1') return;
        sessionStorage.setItem(SW_RELOAD_GUARD_KEY, '1');
      } catch {
        // Ignore storage errors by skipping reload entirely (fail-safe)
        return;
      }

      // Secondary in-memory guard for duplicate controllerchange events.
      if (hasControllerChanged) return;
      hasControllerChanged = true;

      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.ready.then((registration) => {
        // Check for updates every time the app is opened
        void registration.update();

        // Listen for new service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available.
                // Ask waiting SW to activate; controllerchange listener will refresh once.
                registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          }
        });
      });
    }

    return (): void => {
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  useEffect(() => {
    if (!clearOnVisibilityChange && !clearOnFocus) return;

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && clearOnVisibilityChange) {
        if ('serviceWorker' in navigator) {
          void navigator.serviceWorker.ready.then((registration) => {
            void registration.update();
          });
        }
      }
    };

    const handleFocus = (): void => {
      if (clearOnFocus) {
        if ('serviceWorker' in navigator) {
          void navigator.serviceWorker.ready.then((registration) => {
            void registration.update();
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [clearOnVisibilityChange, clearOnFocus]);

  return {
    clearCache: clearAllCaches,
    checkForUpdates: (): void => {
      if ('serviceWorker' in navigator) {
        void navigator.serviceWorker.ready.then((registration) => {
          void registration.update();
        });
      }
    },
  };
}

/**
 * Manually clear all caches
 */
export async function clearAllCaches(): Promise<void> {
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => {
          return caches.delete(cacheName);
        })
      );
    } catch {
      // Intentionally ignore cache deletion errors
    }
  }

  // Also send message to service worker
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    const messageChannel = new MessageChannel();
    const controller = navigator.serviceWorker.controller;

    return new Promise((resolve) => {
      messageChannel.port1.onmessage = (event: MessageEvent<{ success?: boolean }>): void => {
        if (event.data.success) {
          // Cache cleared
        }
        resolve();
      };

      controller.postMessage({ type: 'CLEAR_CACHE' }, [messageChannel.port2]);
    });
  }
}

/**
 * Get cache storage usage
 */
export async function getCacheSize(): Promise<{
  quota: number;
  usage: number;
  percentage: number;
} | null> {
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const percentage = quota > 0 ? (usage / quota) * 100 : 0;

      return {
        quota,
        usage,
        percentage,
      };
    } catch {
      return null;
    }
  }
  return null;
}
